import {
  Alert,
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import React from "react";
import { type OrganizationMessageFieldsFragment } from "../../../generated/graphql";
import {
  audienceFromMessage,
  audienceLabel,
  MESSAGE_TABS,
  type MessageCounts,
  type MessageTab,
  messageHeadline,
  statusDateLabel,
  tabCount,
} from "../message-content";
import { SEVERITY_ICON, StatusChip } from "./message-chips";

const styles = {
  tabs: { borderBottom: 1, borderColor: "divider", px: 1 },
  list: { py: 0 },
  row: { borderBottom: 1, borderColor: "divider", gap: 1.5 },
  headline: { fontWeight: 600 },
  empty: { p: 3, color: "text.secondary", textAlign: "center" },
  loadMore: { p: 1, textAlign: "center" },
} satisfies Record<string, SxProps<Theme>>;

const PRIMARY_TEXT_PROPS = { noWrap: true, sx: styles.headline } as const;
const SECONDARY_TEXT_PROPS = { noWrap: true } as const;

type MessageRowProps = {
  readonly message: OrganizationMessageFieldsFragment;
  readonly selected: boolean;
  readonly onSelect: (uuid: string) => void;
};

const MessageRow = React.memo(
  ({ message, selected, onSelect }: MessageRowProps) => {
    const Icon = SEVERITY_ICON[message.severity];
    return (
      <ListItemButton
        selected={selected}
        sx={styles.row}
        onClick={() => {
          onSelect(message.uuid);
        }}
      >
        <Icon fontSize="small" color="action" />
        <ListItemText
          primary={messageHeadline(message)}
          primaryTypographyProps={PRIMARY_TEXT_PROPS}
          secondary={`${audienceLabel(audienceFromMessage(message))} · ${statusDateLabel(message)}`}
          secondaryTypographyProps={SECONDARY_TEXT_PROPS}
        />
        <StatusChip message={message} />
      </ListItemButton>
    );
  },
);
MessageRow.displayName = "MessageRow";

type MessageListProps = {
  readonly tab: MessageTab;
  /** Null until loaded; tabs then show no number. */
  readonly counts: MessageCounts | null;
  readonly messages: readonly OrganizationMessageFieldsFragment[];
  readonly selectedUuid: string | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMoreError: string | null;
  readonly onTabChange: (tab: MessageTab) => void;
  readonly onSelect: (uuid: string) => void;
  readonly onLoadMore: () => void;
};

/** One tab of messages, newest first, filtered and counted by the server. */
const MessageList = ({
  tab,
  counts,
  messages,
  selectedUuid,
  hasMore,
  loadingMore,
  loadMoreError,
  onTabChange,
  onSelect,
  onLoadMore,
}: MessageListProps) => {
  return (
    <Paper variant="outlined">
      <Tabs
        variant="scrollable"
        value={tab}
        sx={styles.tabs}
        onChange={(_event, value: MessageTab) => {
          onTabChange(value);
        }}
      >
        {MESSAGE_TABS.map((option) => (
          <Tab
            key={option.value}
            value={option.value}
            label={
              counts === null
                ? option.label
                : `${option.label} (${tabCount(option.value, counts)})`
            }
          />
        ))}
      </Tabs>
      {messages.length === 0 ? (
        <Typography sx={styles.empty}>No messages here.</Typography>
      ) : (
        <List sx={styles.list}>
          {messages.map((message) => (
            <MessageRow
              key={message.uuid}
              message={message}
              selected={message.uuid === selectedUuid}
              onSelect={onSelect}
            />
          ))}
        </List>
      )}
      {loadMoreError !== null && (
        <Alert severity="error">
          Could not load older messages: {loadMoreError}
        </Alert>
      )}
      {hasMore && (
        <Box sx={styles.loadMore}>
          <Button disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? "Loading..." : "Load older messages"}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default MessageList;
